import { Divider, Group } from '@lighthouse/bui'
import type { ArgumentsSchema, NodeArg, TypeInstanceMap } from '@lighthouse/core'
import type { NodeTypes } from '@lighthouse/shared'
import { OutputSpecificDataNodeList } from '@lighthouse/shared'
import React from 'react'

import { useFlowContainerContext } from '@/contexts/FlowContext'

import { innerTypeOptions } from '../../constants'
import * as SC from './styles'

// Internal type for compatibility
type OutputOption = {
    id: string
    label: string
    desc: string
    type: TypeInstanceMap
}

type OutputOptions = OutputOption[]

type OutputVariableProps = {
    options?: OutputOptions | ArgumentsSchema[] | NodeArg[]
    nodeType?: NodeTypes
    noMt?: boolean // 有些老组件有向下的padding，此时就不需要mt了，兼容下老组件
}

export const typeName = (type: TypeInstanceMap) => {
    const item = innerTypeOptions.find(item => item.value === type)

    return item?.label
}

// Convert ArgumentsSchema to OutputOptions format for compatibility
const convertArgumentsSchemaToOutputOptions = (schemas: ArgumentsSchema[] | NodeArg[]): OutputOptions => {
    return schemas.map(schema => ({
        id: schema.id,
        label: schema.name,
        desc: 'desc' in schema ? (schema.desc as string) : '',
        type: schema.innerType
    }))
}

const OutputVariable: React.FC<OutputVariableProps> = ({ options, nodeType, noMt = false }) => {
    const { type } = useFlowContainerContext()
    const isInWorkflow = !!type

    if (!isInWorkflow) {
        return null
    }

    // Get options from OutputSpecificDataNodeList if nodeType is provided
    let outputOptions: OutputOptions | undefined

    if (nodeType && nodeType in OutputSpecificDataNodeList) {
        const nodeTypeKey = nodeType as keyof typeof OutputSpecificDataNodeList
        outputOptions = convertArgumentsSchemaToOutputOptions(OutputSpecificDataNodeList[nodeTypeKey])
    } else if (options) {
        // Handle both OutputOptions and ArgumentsSchema[] formats
        if (options.length > 0 && 'innerType' in options[0]) {
            // It's ArgumentsSchema[]
            outputOptions = convertArgumentsSchemaToOutputOptions(options as ArgumentsSchema[])
        } else {
            // It's OutputOptions
            outputOptions = options as OutputOptions
        }
    }

    return (
        <SC.Container>
            <Divider />
            <Group label="输出变量">
                <SC.List>
                    {outputOptions?.map(item => (
                        <SC.ListItem key={item.id}>
                            <SC.Header>
                                <SC.Label>{item.label}</SC.Label>
                                <SC.Type>{typeName(item.type)}</SC.Type>
                            </SC.Header>
                            <SC.Desc>{item.desc}</SC.Desc>
                        </SC.ListItem>
                    ))}
                </SC.List>
            </Group>
        </SC.Container>
    )
}

export default OutputVariable
